import { connectorApiUrl } from '@/config'

type HTTPMethod = 'POST' | 'GET' | 'PATCH' | 'DELETE'

/**
 * A fetch API helper
 *
 * @param method HTTPmethod
 * @param url endpoint url
 * @param idToken 'User-Token' header
 * @param body optional content to be stringified and added
 * @returns the response for the (predefined) fetch call
 */

export function composedFetch(
  method: HTTPMethod = 'GET',
  url: string,
  idToken?: string | null,
  body?: any,
  extraOptions?: any,
): Promise<Response> {
  let options = {
    method: method,
    headers: getHeaders(idToken),
  }
  if (body) {
    const json = JSON.stringify(body)
    options = { ...options, ...{ body: json } }
  }
  if (extraOptions) {
    options = { ...options, ...extraOptions }
  }
  return fetch(url, options as any)
}

function getHeaders(idToken?: string | null): {} {
  const defaultHeader = { 'Content-Type': 'application/json' }
  const header = idToken ? { ...defaultHeader, 'User-Token': idToken } : defaultHeader
  return header
}

// URL helpers
export function getResourcesURL(): string {
  return `${connectorApiUrl}/resources/`
}

export function getShareDrivesURL(): string {
  return `${connectorApiUrl}/share_drives/`
}

export function getShareWorkstationsURL(): string {
  return `${connectorApiUrl}/share_workstations/`
}

export function deleteWorkstationURL(workstationUuid: string): string {
  return `${connectorApiUrl}/workstations/${workstationUuid}`
}

export function getUserRemainingHoursAPIEndpoint(): string {
  return `${connectorApiUrl}/events/usage`
}

export function getWorkstationURL(uuid: string): string {
  return `${connectorApiUrl}/workstations/${uuid}`
}

export function getDrivesURL(uuid: string): string {
  return `${connectorApiUrl}/drives/${uuid}`
}

export function getWaitingListURL(): string {
  return `${connectorApiUrl}/waiting_list/`
}
// currently unused
export function getConfigURL(): string {
  return `${connectorApiUrl}/users/self/config`
}

export function getPublicBundlesURL(): string {
  return `${connectorApiUrl}/config/bundles`
}

export function getPublicWorkstationsURL(): string {
  return `${connectorApiUrl}/config/workstations`
}

export function getUserBundleURL(): string {
  return `${connectorApiUrl}/users/self/bundle`
}

export function getTeradiciConnectEndpoint(workstationUuid: string): string {
  return `${connectorApiUrl}/connect-teradici?uuid=${encodeURIComponent(workstationUuid)}`
}

/**
 * A helper to get an endpoint URL. Thanks to this
 * there is no need to import connectorApiUrl to each
 * of API request files.
 *
 * @param route A route towards a specific enpoint
 * @returns a full connector endpoint
 * @note no following slash included!
 */
export function getEndpointURL(route: string): string {
  return `${connectorApiUrl}/${route}`
}

/**
 * From a full file path get a folder path without the filename.
 * @param url Full path
 * @returns The path without the file name
 */
export function getPathLeadingToFile(url: string): string {
  const SEPARATOR = '/'

  if (!url.includes(SEPARATOR)) return url

  const layers = url.split(SEPARATOR)
  layers.pop()

  return layers.join(SEPARATOR)
}

/**
 * Get a Router link "to" argument to get to file in browser
 * @param driveUuid drive uuid
 * @param path path to file
 * @returns a use-ready RouterLink "path" (it consiously is missing root)
 */
export function getUrlToBrowsePath(driveUuid: string, path: string): string {
  return `drives/${driveUuid}/${path}`
}

/**
 * NOT USED - apparently it takes more to convert paths
 *
 * JS Sets do not get JSON.stringified properly into Request Body,
 * so here is a converter.
 * @param object Any object that might have fields of Set type
 * @returns the same object, but with said fields turned into arrays.
 */
export function arrayzeSetFields(object: any): any {
  const copy = { ...object }
  for (const [key, value] of Object.entries(copy)) {
    if (value instanceof Set) {
      copy[key] = Array.from(value)
    }
  }
  return copy
}

/**
 * Get a set of strings and turn it into an array of single-keyed objects.
 *
 * Mailny used to convert Set<string> file paths into API-compatible Array<{"path": string}>
 *
 * This is necessary
 * @param a_set A set to be arrayzed
 * @param propName a key name to assign the value
 * @returns an array with one-keyed objects
 */
export function arrayzeAndExpand(
  a_set: Set<string>,
  propName: string = 'path',
): { [key: string]: string }[] {
  const our_array = Array.from(a_set)
  return our_array.map((item: string) => ({ [propName]: item }))
}

export function updateRootVolumeWorkstationURL(workstationUuid: string): string {
  return `${connectorApiUrl}/workstations/${workstationUuid}/root_storage`
}